// Il market di «Aggiungi strumento» (tela «PopTown Plancia 2», artboard 44): ricerca, filtri per
// categoria e una tessera per tipologia. Con le tipologie che crescono, un elenco di radio non
// basta più. La tessera dice anche quante connessioni di quel tipo ci sono già: ogni tipo si
// collega più volte, con nomi e permessi diversi.
import { useMemo, useState } from 'react';
import type { ToolKind } from '../../lib/api';
import { Icon } from '../../components/Icon';

const ALL = 'Tutti';

// il colore della tessera per categoria, solo da token: leggibile in entrambi i temi
const TONE: Record<string, [string, string]> = {
  Memoria: ['var(--violet-soft)', 'var(--violet)'],
  CLI: ['var(--neutral-soft)', 'var(--text)'],
  Generici: ['var(--info-soft)', 'var(--info)'],
  Posta: ['var(--danger-soft)', 'var(--danger)'],
  Notifiche: ['var(--accent-soft)', 'var(--accent)'],
};

function matches(k: ToolKind, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || [k.label, k.hint, k.category, k.id].some((t) => t.toLowerCase().includes(q));
}

export function ToolMarket({
  kinds,
  linked,
  onPick,
}: {
  kinds: ToolKind[];
  /** quante connessioni ci sono già, per tipologia */
  linked: Record<string, number>;
  onPick: (kind: ToolKind) => void;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ALL);
  const categories = useMemo(() => [ALL, ...new Set(kinds.map((k) => k.category))], [kinds]);
  const shown = kinds.filter(
    (k) => (category === ALL || k.category === category) && matches(k, query),
  );
  const note = query.trim()
    ? `${shown.length} ${shown.length === 1 ? 'risultato' : 'risultati'} per «${query.trim()}»`
    : `${shown.length} strumenti · ognuno si può collegare più volte, con nomi e permessi diversi`;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 40,
          padding: '0 12px',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)',
          background: 'var(--panel)',
        }}
      >
        <Icon name="search" size={16} />
        <input
          aria-label="Cerca fra gli strumenti"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca fra gli strumenti: gmail, notifiche, memoria…"
          autoFocus
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'var(--text)',
            font: '400 14px/20px var(--font-ui)',
          }}
        />
      </label>
      <div
        role="group"
        aria-label="Categorie"
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
      >
        {categories.map((c) => {
          const on = c === category;
          return (
            <button
              key={c}
              type="button"
              aria-pressed={on}
              onClick={() => setCategory(c)}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 'var(--r-full)',
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
                background: on ? 'var(--accent-soft)' : 'transparent',
                color: on ? 'var(--accent)' : 'var(--text)',
                font: '500 13px/18px var(--font-ui)',
                cursor: 'pointer',
              }}
            >
              {c}
            </button>
          );
        })}
      </div>
      <div className="t-caption dim" aria-live="polite">
        {note}
      </div>
      {shown.length === 0 ? (
        <div className="t-small dim" style={{ padding: '24px 0', textAlign: 'center' }}>
          Nessuno strumento corrisponde. Prova un'altra parola o la categoria «Tutti».
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: 12,
          }}
        >
          {shown.map((k) => (
            <Tile key={k.id} kind={k} count={linked[k.id] ?? 0} onPick={() => onPick(k)} />
          ))}
        </div>
      )}
    </div>
  );
}

function Tile({ kind: k, count, onPick }: { kind: ToolKind; count: number; onPick: () => void }) {
  const [bg, fg] = TONE[k.category] ?? ['var(--neutral-soft)', 'var(--dim)'];
  const badge = !k.enabled
    ? 'In arrivo'
    : count > 0
      ? `${count} ${count === 1 ? 'collegato' : 'collegati'}`
      : null;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!k.enabled}
      style={{
        textAlign: 'left',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-xl)',
        background: 'var(--panel)',
        color: 'var(--text)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 150,
        cursor: k.enabled ? 'pointer' : 'not-allowed',
        opacity: k.enabled ? 1 : 0.55,
        font: 'inherit',
      }}
    >
      <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--r-lg)',
            background: bg,
            color: fg,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon name={k.icon} size={22} />
        </span>
        {badge && (
          <span
            className="t-caption dim"
            style={{
              alignSelf: 'flex-start',
              background: 'var(--neutral-soft)',
              padding: '2px 7px',
              borderRadius: 'var(--r-full)',
              fontWeight: 600,
            }}
          >
            {badge}
          </span>
        )}
      </span>
      <span className="t-body" style={{ fontWeight: 600 }}>
        {k.label}
      </span>
      <span className="t-caption dim">{k.hint}</span>
    </button>
  );
}
